import { useEffect, useState } from 'react';
import { academyApi } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Award, Search, Loader2, Users } from 'lucide-react';

function VerifyCertificate() {
  const [number, setNumber] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const verify = async (e) => {
    e.preventDefault();
    if (!number.trim()) return;
    setLoading(true);
    try {
      setResult((await academyApi.verifyCertificate(number.trim())).data);
    } catch (err) {
      setResult(err.response?.status === 404 ? { valid: false } : { error: true });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={verify} className="flex flex-wrap items-center gap-2 mt-4" data-testid="academy-verify-form">
      <Input value={number} onChange={e => setNumber(e.target.value)} placeholder="Kontrollera intyg, t.ex. VA-2026-1A2B3C" className="w-64 h-9 text-sm" data-testid="academy-verify-input" />
      <Button type="submit" size="sm" variant="outline" disabled={loading} data-testid="academy-verify-btn">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4 mr-1" />}Kontrollera
      </Button>
      {result && (
        <p className={`text-xs font-ibm ${result.valid ? 'text-emerald-700' : 'text-red-600'}`} data-testid="academy-verify-result">
          {result.error ? 'Kunde inte kontrollera intyget – försök igen' : result.valid ? `Giltigt intyg: ${result.name} (${result.role}, ${result.school || '–'}) – ${String(result.issued_at).slice(0, 10)}` : 'Intyget hittades inte'}
        </p>
      )}
    </form>
  );
}

export function AcademyTeam() {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    academyApi.team().then(res => setRows(res.data.users)).catch(() => setRows([]));
  }, []);

  const doneCount = rows?.filter(r => r.completed).length || 0;

  return (
    <section data-testid="academy-team">
      <div className="flex items-center gap-3 mb-1">
        <Users className="h-5 w-5 text-red-500" />
        <h2 className="font-chivo font-black text-lg text-navy dark:text-white">Personalens framsteg</h2>
      </div>
      <p className="text-xs text-slate-400 font-ibm mb-4">
        {rows ? `${doneCount} av ${rows.length} användare har slutfört Vapelarm Academy.` : 'Laddar…'}
      </p>
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-x-auto">
        {!rows ? (
          <div className="p-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : rows.length === 0 ? (
          <p className="p-6 text-sm text-slate-400 font-ibm text-center">Inga användare hittades.</p>
        ) : (
          <table className="w-full text-sm font-ibm">
            <thead className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100 dark:border-slate-700">
              <tr><th className="px-4 py-3">Namn</th><th className="px-4 py-3">Roll</th><th className="px-4 py-3 hidden md:table-cell">Skola</th><th className="px-4 py-3 w-48">Framsteg</th><th className="px-4 py-3">Intyg</th></tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-b last:border-0 border-slate-100 dark:border-slate-700" data-testid={`academy-team-row-${r.id}`}>
                  <td className="px-4 py-3"><p className="font-semibold text-navy dark:text-white">{r.name}</p><p className="text-xs text-slate-400">{r.email}</p></td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{r.role}</td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300 hidden md:table-cell">{r.school || '–'}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Progress value={r.total ? (r.done / r.total) * 100 : 0} className="h-1.5 flex-1 [&>div]:bg-emerald-500" />
                      <span className="text-xs text-slate-500 whitespace-nowrap">{r.done}/{r.total}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3" data-testid={`academy-team-cert-${r.id}`}>
                    {r.certificate_number ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700"><Award className="h-3.5 w-3.5" />{r.certificate_number}</span>
                    ) : <span className="text-xs text-slate-400">Ej klar</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <VerifyCertificate />
    </section>
  );
}
